import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import dashboardService, { AnalisisPresupuestalRubro, AnalisisPresupuestalMensual } from '../services/dashboard.service';
import { formatCurrency } from '../utils/currency';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const AnalisisPresupuestal: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [analisisPorRubro, setAnalisisPorRubro] = useState<AnalisisPresupuestalRubro[]>([]);
  const [analisisMensual, setAnalisisMensual] = useState<AnalisisPresupuestalMensual[]>([]);
  const [tipoFiltro, setTipoFiltro] = useState<'todos' | 'ingreso' | 'gasto'>('todos');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [porRubro, mensual] = await Promise.all([
        dashboardService.getAnalisisPresupuestal(year),
        dashboardService.getAnalisisPresupuestalMensual(year)
      ]);
      setAnalisisPorRubro(porRubro);
      setAnalisisMensual(mensual);
    } catch (error) {
      console.error('Error fetching budget analysis:', error);
      alert('Error al cargar el análisis presupuestal');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (porcentaje: number) => {
    if (porcentaje >= 90 && porcentaje <= 110) return 'text-green-600';
    if (porcentaje >= 70 && porcentaje < 90) return 'text-yellow-600';
    if (porcentaje > 110 && porcentaje <= 130) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusBg = (porcentaje: number) => {
    if (porcentaje >= 90 && porcentaje <= 110) return 'bg-green-50';
    if (porcentaje >= 70 && porcentaje < 90) return 'bg-yellow-50';
    if (porcentaje > 110 && porcentaje <= 130) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  // Filtrar rubros según el tipo seleccionado
  const rubrosFiltrados = analisisPorRubro.filter(r => 
    tipoFiltro === 'todos' || r.rubroTipo === tipoFiltro
  );

  // Preparar datos para gráficos
  const dataMensualIngresos = analisisMensual.map((m, index) => ({
    mes: MESES[m.mes - 1],
    presupuestado: m.presupuestoIngreso,
    ejecutado: m.ingresoReal,
    comprometido: m.ingresoComprometido,
    proyectado: m.ingresoProyectado
  }));

  const dataMensualGastos = analisisMensual.map((m, index) => ({
    mes: MESES[m.mes - 1],
    presupuestado: m.presupuestoGasto,
    ejecutado: m.gastoReal,
    comprometido: m.gastoComprometido,
    proyectado: m.gastoProyectado
  }));

  // Calcular totales
  const totales = rubrosFiltrados.reduce((acc, r) => ({
    presupuestado: acc.presupuestado + r.totalPresupuestado,
    ejecutado: acc.ejecutado + r.totalEjecutado,
    comprometido: acc.comprometido + r.totalComprometido,
    proyectado: acc.proyectado + r.totalProyectado
  }), { presupuestado: 0, ejecutado: 0, comprometido: 0, proyectado: 0 });

  const porcentajeEjecucionTotal = totales.presupuestado > 0 
    ? (totales.ejecutado / totales.presupuestado) * 100 
    : 0;
  const porcentajeProyectadoTotal = totales.presupuestado > 0
    ? (totales.proyectado / totales.presupuestado) * 100
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Cargando análisis presupuestal...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Análisis Presupuestal</h1>
          <div className="flex space-x-4">
            <select
              value={tipoFiltro}
              onChange={(e) => setTipoFiltro(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="todos">Todos los Rubros</option>
              <option value="ingreso">Solo Ingresos</option>
              <option value="gasto">Solo Gastos</option>
            </select>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          Compara lo presupuestado vs ejecutado (real) y comprometido (pagos programados pendientes)
        </p>
      </div>

      {/* Resumen Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {/* Presupuestado */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-500 text-white">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Presupuestado</dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(totales.presupuestado)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Ejecutado */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-500 text-white">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Ejecutado (Real)</dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(totales.ejecutado)}
                  </dd>
                  <dd className={`text-sm ${getStatusColor(porcentajeEjecucionTotal)}`}>
                    {porcentajeEjecucionTotal.toFixed(1)}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Comprometido */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-yellow-500 text-white">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Comprometido</dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatCurrency(totales.comprometido)}
                  </dd>
                  <dd className="text-sm text-gray-500">
                    Pagos programados
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Proyectado */}
        <div className={`overflow-hidden shadow rounded-lg ${getStatusBg(porcentajeProyectadoTotal)}`}>
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`flex items-center justify-center h-12 w-12 rounded-md ${
                  porcentajeProyectadoTotal >= 90 && porcentajeProyectadoTotal <= 110 ? 'bg-green-600' : 'bg-yellow-600'
                } text-white`}>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Proyectado</dt>
                  <dd className={`text-lg font-semibold ${getStatusColor(porcentajeProyectadoTotal)}`}>
                    {formatCurrency(totales.proyectado)}
                  </dd>
                  <dd className={`text-sm ${getStatusColor(porcentajeProyectadoTotal)}`}>
                    {porcentajeProyectadoTotal.toFixed(1)}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
        {/* Gráfico de Ingresos */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Ingresos: Presupuestado vs Ejecutado vs Proyectado</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dataMensualIngresos}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="presupuestado" fill="#3B82F6" name="Presupuestado" />
              <Bar dataKey="ejecutado" fill="#10B981" name="Ejecutado (Real)" />
              <Bar dataKey="comprometido" fill="#F59E0B" name="Comprometido" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de Gastos */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Gastos: Presupuestado vs Ejecutado vs Proyectado</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dataMensualGastos}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="presupuestado" fill="#3B82F6" name="Presupuestado" />
              <Bar dataKey="ejecutado" fill="#EF4444" name="Ejecutado (Real)" />
              <Bar dataKey="comprometido" fill="#F59E0B" name="Comprometido" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Gráfico de Línea: Ejecución Mensual */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Porcentaje de Ejecución Mensual</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={analisisMensual.map((m, i) => ({
            mes: MESES[m.mes - 1],
            ingresos: m.porcentajeEjecucionIngreso,
            gastos: m.porcentajeEjecucionGasto
          }))}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="mes" />
            <YAxis />
            <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
            <Legend />
            <Line type="monotone" dataKey="ingresos" stroke="#10B981" strokeWidth={2} name="% Ejecución Ingresos" />
            <Line type="monotone" dataKey="gastos" stroke="#EF4444" strokeWidth={2} name="% Ejecución Gastos" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Tabla de Detalles por Rubro */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Detalle por Rubro</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rubro
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Presupuestado
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ejecutado
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comprometido
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Proyectado
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % Ejecución
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  % Proyectado
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Desviación
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rubrosFiltrados.map((rubro) => (
                <tr key={rubro.rubroId}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {rubro.rubroCodigo ? `${rubro.rubroCodigo} - ` : ''}{rubro.rubroNombre}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      rubro.rubroTipo === 'ingreso' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {rubro.rubroTipo}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {formatCurrency(rubro.totalPresupuestado)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                    {formatCurrency(rubro.totalEjecutado)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-yellow-600">
                    {formatCurrency(rubro.totalComprometido)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                    {formatCurrency(rubro.totalProyectado)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${getStatusColor(rubro.porcentajeEjecucion)}`}>
                    {rubro.porcentajeEjecucion.toFixed(1)}%
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${getStatusColor(rubro.porcentajeProyectado)}`}>
                    {rubro.porcentajeProyectado.toFixed(1)}%
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                    rubro.desviacion >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(rubro.desviacion)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={2} className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                  TOTAL:
                </td>
                <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                  {formatCurrency(totales.presupuestado)}
                </td>
                <td className="px-6 py-3 text-right text-sm font-bold text-green-600">
                  {formatCurrency(totales.ejecutado)}
                </td>
                <td className="px-6 py-3 text-right text-sm font-bold text-yellow-600">
                  {formatCurrency(totales.comprometido)}
                </td>
                <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                  {formatCurrency(totales.proyectado)}
                </td>
                <td className={`px-6 py-3 text-right text-sm font-bold ${getStatusColor(porcentajeEjecucionTotal)}`}>
                  {porcentajeEjecucionTotal.toFixed(1)}%
                </td>
                <td className={`px-6 py-3 text-right text-sm font-bold ${getStatusColor(porcentajeProyectadoTotal)}`}>
                  {porcentajeProyectadoTotal.toFixed(1)}%
                </td>
                <td className={`px-6 py-3 text-right text-sm font-bold ${
                  (totales.ejecutado - totales.presupuestado) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(totales.ejecutado - totales.presupuestado)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalisisPresupuestal;
